import ErrorNote from './ErrorNote'
import { describeClass } from '../api/classDescription'
import { type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  addMonths,
  closureHeadline,
  fetchFilterOptions,
  fetchMonth,
  monthOf,
  today,
  type MonthDay,
} from '../api/calendar'
import { useCalendarFilter, withFilter } from '../useCalendarFilter'
import CalendarFilters from './CalendarFilters'
import { type CalendarView } from './CalendarViewSwitcher'
import CalendarLink from './CalendarLink'
import ScheduleHeader from './ScheduleHeader'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * One cell in the month grid.
 *
 * A count and a few names rather than every class: the real timetable is around
 * 99 scheduled sessions a week, so a month of individual blocks would be a wall
 * of roughly 400 items. This answers what a month view is actually for — which
 * weeks are heavy, and why the studio is shut that Thursday.
 */
function DayCell({
  day,
  isToday,
  params,
  onOpenDay,
}: {
  day: MonthDay
  isToday: boolean
  params: URLSearchParams
  /** Supplied when embedded, so tapping a day does not route away (D-312). */
  onOpenDay?: (date: string) => void
}) {
  const dayNumber = Number(day.date.slice(-2))

  return (
    <CalendarLink
      to={withFilter(`/calendar/day/${day.date}`, params)}
      onActivate={onOpenDay ? () => onOpenDay(day.date) : undefined}
      className={`flex min-h-[104px] w-full flex-col rounded-lg border p-2 text-left no-underline transition-colors ${
        isToday ? 'border-accent' : 'border-line'
      } ${day.isInMonth ? 'bg-surface hover:border-ink-3' : 'bg-surface-2 opacity-55'}`}
    >
      <div className="mb-1 flex items-baseline justify-between">
        <span
          className={`tabular text-[13px] font-semibold ${
            isToday ? 'text-accent' : day.isInMonth ? 'text-ink' : 'text-ink-3'
          }`}
        >
          {dayNumber}
        </span>
        {day.classCount > 0 && (
          <span className="tabular text-[10.5px] font-semibold text-ink-3">
            {day.classCount}
          </span>
        )}
      </div>

      {/*
        <b>A day shut from six is not a shut day, and the cell has to show it</b>
        (D-741, scenario 28).

        A whole-day closure replaces the class list, which is right - nothing
        runs. A partial one did the same, so a Tuesday closed from 5pm rendered
        exactly like a Tuesday closed all day and the 4:30 classes that still
        run vanished from the month. The distinction is the previews staying,
        not a different shade of the same block.

        <b>And a closure that only cancels the regular schedule is the same
        shape</b> (D-868). It is a whole-day closure, so it took the
        nothing-runs branch above and the one-off class and the private that are
        still on vanished - this bug, one dimension over, found by an audit.
        Anything that leaves sessions running keeps its previews.
      */}
      {day.isClosed && day.closedFrom === null && !day.regularScheduleOnly ? (
        <div className="rounded bg-warn-bg px-1.5 py-1 text-[10.5px] leading-tight font-semibold text-warn">
          Closed
          {day.closureReason && <div className="font-normal">{day.closureReason}</div>}
        </div>
      ) : day.isClosed ? (
        <div className="flex flex-col gap-0.5">
          {/*
            An outline rather than a filled block, so a glance down the month
            separates "shut" from "shut later" without reading either.

            The regular-schedule wording says which classes went, because
            "Closed" over a list of things that are running reads as a mistake.
          */}
          <div className="rounded border border-warn px-1.5 text-[10.5px] leading-tight font-semibold text-warn">
            {closureHeadline(day.closedFrom, day.regularScheduleOnly, 'Closed')}
          </div>
          <Previews day={day} />
        </div>
      ) : day.classCount === 0 ? (
        <span className="text-[10.5px] text-ink-3">—</span>
      ) : (
        <Previews day={day} />
      )}

      {day.cancelledCount > 0 && (
        <span className="mt-auto pt-1 text-[10px] font-semibold text-crit">
          {day.cancelledCount} cancelled
        </span>
      )}
    </CalendarLink>
  )
}

/**
 * The few names that give a month cell its meaning.
 *
 * <b>One list, called from two branches</b> (A-17). A partly closed day keeps
 * its classes - the ones before the cutoff still run - and an ordinary day is
 * the same list with no banner above it. Written inline in both, the second
 * copy immediately lost the room tint and the truncation, which are three
 * separate layout findings the comments below record.
 */
function Previews({ day }: { day: MonthDay }) {
  return (
    <div className="flex flex-col gap-0.5">
      {day.preview.map((item) => (
        <span
          key={item.className}
          className="flex items-center gap-1.5 text-[10.5px] leading-tight text-ink-2"
        >
          {/*
            Colour lives on the dot, not the text. Coloured text at this
            size is hard to read, and three colours of text in one cell
            looks like clutter rather than a system.
          */}
          <span
            className="room-tint size-1.5 shrink-0 rounded-full"
            style={
              {
                '--room-color': item.roomColor ?? 'var(--color-line-2)',
                background: 'var(--room-display)',
              } as React.CSSProperties
            }
            aria-hidden="true"
          />
          {/*
            min-w-0 or truncate does nothing: a flex item's min-width
            defaults to its content, so a long unbroken class name pushes
            the cell wider instead of clipping. Truncated rather than
            wrapped here, unlike the week view — a month cell has a fixed
            height and is a glance, not a read.

            **And truncation is exactly why this reads the name** (D-579).
            A month cell is about 90px wide, so a raw Jackrabbit string was
            clipped to `COMP_TECH_B…` — and the distinguishing part of one
            of those names is at the *end*, so what survived the clip was
            the part every neighbouring row shares. *Ballet* fits whole.
          */}
          <span className="min-w-0 truncate" title={item.className}>
            {monthLabel(item.className)}
          </span>
        </span>
      ))}
      {day.classCount > day.preview.length && (
        <span className="pl-3 text-[10.5px] text-ink-3">
          +{day.classCount - day.preview.length} more
        </span>
      )}
    </div>
  )
}

/**
 * A month cell's version of a class name (D-579).
 *
 * <b>The label alone, without the dancer.</b> The week card says
 * <i>Solo &middot; Hannah</i> because two solos can share a room and an hour
 * and the dancer is what tells them apart; a month cell shows at most three of
 * a day's classes and is a glance rather than a read, so the extra name buys
 * nothing here and costs the room the label needs.
 */
export function monthLabel(className: string): string {
  return describeClass(className).label
}

export default function CalendarMonthView({
  date: anchor,
  onDate: setAnchor,
  onView,
  actions,
  teachingOnly = false,
}: {
  /**
   * Show only what she teaches, leaving her own children's classes out
   * (D-374). Set by `TeacherSchedule` for *My classes*; off everywhere else,
   * which is the union home needs.
   */
  teachingOnly?: boolean
  /**
   * Any day in the month on screen, owned by the caller (D-360).
   *
   * A date rather than a `{ year, month }` pair, so all three calendar views
   * take the same thing and a caller can hand a week's anchor to the month
   * without converting it — which is exactly what switching view does.
   */
  date: string
  onDate: (date: string) => void
  /**
   * What a view change does. **Required** (D-395) — D-312 made it optional, and
   * the absent case routed to `/calendar/*` from inside the switcher, so an
   * embed that forgot it offered a parent controls landing on "Not your page".
   * The route wrappers navigate now, as they already do for `onDate`.
   */
  onView: (view: CalendarView, date: string) => void
  /**
   * Buttons that belong with the date controls rather than above them (D-321).
   *
   * "Book a room" started life as its own row over the calendar and read as a
   * banner. It is the same kind of thing as Week/Day/Month — something you do
   * to this view — so it sits in the same line, to the left of the switcher.
   */
  actions?: ReactNode
}) {
  const [urlFilter, setFilter] = useCalendarFilter()
  /*
   * Which half of her schedule, folded into the filter so the react-query key
   * moves with it (D-374). Set by `TeacherSchedule` for *My classes*; off
   * everywhere else, which is the union.
   */
  const filter = teachingOnly ? { ...urlFilter, teachingOnly: true } : urlFilter

  const [searchParams] = useSearchParams()
  const { year, month } = monthOf(anchor)

  // Any day in the month identifies it; the first is the one the picker
  // highlights and the one a view change hands on.
  const firstOf = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}-01`

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['calendar', 'month', year, month, filter],
    queryFn: () => fetchMonth(year, month, filter),
    placeholderData: (previous) => previous,
  })

  // Options come from the first day of the month rather than the whole span:
  // one week's worth of rooms and teachers is representative, and it avoids a
  // second month-wide query just to populate chips.
  const { data: options } = useQuery({
    queryKey: ['calendar', 'filters', `${year}-${month}`, filter.wholeStudio, filter.teachingOnly],
    queryFn: () => fetchFilterOptions(`${year}-${String(month).padStart(2, '0')}-15`, { wholeStudio: filter.wholeStudio, teachingOnly: filter.teachingOnly }),
  })

  const matched = data?.days.reduce((n, d) => n + (d.isInMonth ? d.classCount : 0), 0) ?? 0

  const step = (delta: number) => {
    const next = addMonths(year, month, delta)
    setAnchor(firstOf(next.year, next.month))
  }

  /*
   * Mid-month, not the first, when a view change hands the date on. Switching
   * from October to Week on the 1st lands on a week that is mostly September.
   */
  const handOn = `${year}-${String(month).padStart(2, '0')}-15`

  return (
    <div className="page-shell px-5 py-8">
      <ScheduleHeader
        eyebrow="Master calendar"
        label={data?.monthName ?? 'Loading...'}
        anchor={firstOf(year, month)}
        /*
          Both branches, and the first cut gave it to only one. Handing the
          embedded caller the 15th while the switcher's own navigation kept the
          anchor meant Tonya tapping *Week* from October landed on Sep 28 - Oct
          4, which is the very thing the comment above says it prevents.
        */
        view="month"
        onPick={setAnchor}
        onView={(view) => onView(view, handOn)}
        onStep={step}
        onToday={() => setAnchor(today())}
        actions={actions}
        hideTeachers={Boolean(onView)}
        subtitle={
          data && (
            <p className="mt-1 text-sm text-ink-3">
              {data.totalClasses} sessions this month &middot; click a day for the room grid
            </p>
          )
        }
      />

      <CalendarFilters
        options={options}
        filter={filter}
        onChange={setFilter}
        matched={matched}
        total={matched}
      />

      {isError && (
        <ErrorNote boxed>
          {(error as Error).message}
        </ErrorNote>
      )}

      {isPending && <p className="text-sm text-ink-3">Loading…</p>}

      {data && (
        <>
          <div className="mb-1.5 grid grid-cols-7 gap-2">
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className="px-1 text-[10.5px] font-bold tracking-[0.11em] text-ink-3 uppercase"
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {data.days.map((day) => (
              <DayCell
                key={day.date}
                day={day}
                isToday={day.date === today()}
                params={searchParams}
                onOpenDay={onView ? (date) => onView("day", date) : undefined}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
